import { omit } from 'lodash';
import * as Sentry from '@sentry/react';
import { userStore } from 'stores';

/**
 * Разделение массива структур (объектов) по типу и их перегруппировка
 * в массив массивов по типу.
 *
 * Решение частой задачи, когда с бэка приходит плоский массив структур,
 * который надо перегруппировать в несколько массивов по какому-либо полю.
 *
 * Иллюстративный пример: плоский массив данных надо перегруппировать во
 * фронтенде в аккордеон по какой-либо группировке.
 *
 * В реализации данного решения сделано несколько допущений, сделанных
 * относительно бизнес-логике приложения:
 * 1) Каждый объект во входном плоском массиве имеет поле type,
 * который является структурой типа { id: ..., name: ... }
 * 2) В ответе вложенное подмножество нах-ся в поле files, т. к. в абсолютном
 * большинстве задач - это обработка на экранах файлов (документы, фото)
 *
 * Все эти допущения сделаны, т.к. универсальное решение будет слишком
 * громоздким и плохочитаемым, а в данном виде решает 99% встречающихся
 * задач.
 * @param {*} arraysObjects - плоский массив объектов с обязательным полем
 * type, который является структурой вида { id: ..., name: ... }
 * @returns массив объектов вида:
 *
 * {
 *
 *    id: уникальное значение из структуры type входного массива;
 *
 *    name: значение name, соответствующее id из структуры type входного массива;
 *
 *    files: массив-подмножество входного массива с одинаковым type.id;
 *
 * }
 */
export const splittingArraysObjectsByTypes = (arraysObjects) => {
  // проверка контракта метода (входных допущений)
  const handleSentryContractError = () => {
    Sentry.withScope((scope) => {
      scope.setExtra('inputArrayOfObjects', JSON.stringify(arraysObjects));
      const { user } = userStore;
      if (user?.uname ?? user?.username) {
        scope.setUser({ username: user.uname ?? user.username });
      }
      Sentry.captureException(
        new Error(
          'В одном из входных объектов не была найдена структура type либо у структуры type невалидный id.',
        ),
      );
    });
  };

  if (!arraysObjects.find((item) => 'type' in item && !!item.type.id)) {
    handleSentryContractError();
  }

  // алгоритм
  const idsTypes = [...new Set(arraysObjects.map((obj) => obj.type.id))];
  // eslint-disable-next-line prefer-const
  let typesArray = [];
  idsTypes.forEach((typeId) => {
    const objectsByType = arraysObjects.filter((obj) => obj.type.id === typeId);
    typesArray.push({
      id: typeId,
      name: objectsByType[0].type.name,
      files: objectsByType.map((obj) => ({ ...omit(obj, ['type']) })),
    });
  });

  return typesArray;
};
